/**
 * @fileoverview Hover-to-dropdown bridge for mega menu navigation.
 * Works with <dropdown-element> — these components handle hover logic only,
 * delegating open/close to the dropdown's existing window event API.
 *
 * @example
 * // Basic mega menu with two nav items
 * <mega-menu-container close-delay="400">
 *   <nav>
 *     <mega-menu-trigger dropdown-id="mega-shop">
 *       <a href="/collections">Shop</a>
 *     </mega-menu-trigger>
 *     <mega-menu-trigger dropdown-id="mega-about">
 *       <a href="/about">About</a>
 *     </mega-menu-trigger>
 *   </nav>
 *
 *   <dropdown-element id="mega-shop">
 *     <dropdown-panel>
 *       <div>Shop mega menu content</div>
 *     </dropdown-panel>
 *   </dropdown-element>
 *
 *   <dropdown-element id="mega-about">
 *     <dropdown-panel>
 *       <div>About mega menu content</div>
 *     </dropdown-panel>
 *   </dropdown-element>
 * </mega-menu-container>
 *
 * @example
 * // Custom close delay
 * <mega-menu-container close-delay="600">
 *   ...
 * </mega-menu-container>
 */

class MegaMenuContainer extends HTMLElement {
  #activeDropdownId = null;
  #closeTimer = null;

  connectedCallback() {
    this.addEventListener('mouseenter', () => {
      this.#cancelClose();
    });

    this.addEventListener('mouseleave', () => {
      this.#scheduleClose();
    });

    // A panel can close without going through this container — a click on its own nav item
    // (the item sits outside <dropdown-element>, so the dropdown's click-outside handler
    // catches it), Escape, a click anywhere else on the page. Listening to the dropdown's
    // own events keeps #activeDropdownId honest; otherwise the container goes on believing
    // a closed panel is open and hovering that item again is a no-op (openDropdown
    // early-returns) until the cursor leaves the whole nav.
    this.addEventListener('dropdown:open', (event) => {
      const id = event.target?.id;
      if (id) this.#activeDropdownId = id;
    });

    this.addEventListener('dropdown:close', (event) => {
      if (event.target?.id === this.#activeDropdownId) this.#activeDropdownId = null;
    });
  }

  disconnectedCallback() {
    this.#cancelClose();
  }

  /**
   * Opens a dropdown by ID. Closes any other active dropdown instantly.
   * @param {string} id - The dropdown element ID to open
   */
  openDropdown(id) {
    if (this.#activeDropdownId === id) return;

    // Close current dropdown instantly (no delay)
    if (this.#activeDropdownId) {
      window.dispatchEvent(new Event(`close-dropdown-${this.#activeDropdownId}`));
    }

    this.#cancelClose();
    this.#activeDropdownId = id;
    window.dispatchEvent(new Event(`open-dropdown-${id}`));
  }

  /** Closes the active dropdown immediately */
  closeActive() {
    if (!this.#activeDropdownId) return;
    window.dispatchEvent(new Event(`close-dropdown-${this.#activeDropdownId}`));
    this.#activeDropdownId = null;
  }

  #scheduleClose() {
    this.#cancelClose();
    const delay = parseInt(this.getAttribute('close-delay'), 10) || 400;
    this.#closeTimer = setTimeout(() => {
      this.closeActive();
    }, delay);
  }

  #cancelClose() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }
}

class MegaMenuTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('mouseenter', () => {
      const dropdownId = this.getAttribute('dropdown-id');
      if (!dropdownId) return;

      const container = this.closest('mega-menu-container');
      if (container) {
        container.openDropdown(dropdownId);
      }
    });

    // A trigger with no link has nothing to do on click. Left alone, that click still
    // reaches the dropdown's document-level click-outside handler (the trigger sits
    // outside <dropdown-element>) and dismisses the panel the cursor is still sitting on,
    // which reads as the menu breaking. Keep the click here so an inert label does nothing.
    // A linked label is left alone: the click navigates, and dismissing the panel is right.
    this.addEventListener('click', (event) => {
      if (!this.querySelector('a[href]')) event.stopPropagation();
    });
  }
}

customElements.define('mega-menu-container', MegaMenuContainer);
customElements.define('mega-menu-trigger', MegaMenuTrigger);
